import { FILTER_TYPES } from "./utils/filterMapper";
import type { BucketRange } from "@/features/control-tower/interfaces/update-widget";

export type FromToDateRange = {
  from: string;
  to: string;
};

export type StartEndDateRange = {
  startDate: string;
  endDate: string;
};

interface DateRange {
  dateRange?: FromToDateRange | StartEndDateRange;
}
/** Represents a single selectable option for select or typeahead */
export interface FilterOption extends DateRange {
  label: string;
  value: string | number;
  filterType?: FilterTypeEnum.PANEL_FILTERS | FilterTypeEnum.QUICK_FILTERS; // This can be used to differentiate between quick and panel filters
  disabled?: boolean;
}

/** Represents the saved selection for a filter */
export interface FilterSelection extends DateRange {
  label: string;
  value: string | number;
}

export interface SelectOption {
  label: string;
  dateRange?: {
    from: string;
    to: string;
  };
  value: string | number;
}

/** Common structure for all filters */
export interface FilterDefinition {
  key: string;
  type: FilterComponentType;
  label?: string;
  default?: FilterSelection | FilterSelection[] | string; // Can be a single selection or multiple selections
  placeholder?: string;
  hiddenlabel?: boolean;
  suffix?: string;
  value?: FilterSelection | FilterSelection[] | string | number | BucketRange[]; // Can be a single selection or multiple selections
  fromLabel?: string; // e.g., "From"
  toLabel?: string; // e.g., "To"
  fromPlaceholder?: string; // e.g., "Select date from"
  toPlaceholder?: string; // e.g., "Select date to"
  fromMonthYearPlaceholder?: string; // e.g., "YYYY-MM"
  toMonthYearPlaceholder?: string; // e.g., "YYYY-MM"
  fromMonthYearLabel?: string; // e.g., "From Month-Year"
  toMonthYearLabel?: string; // e.g., "To Month-Year"

  // For dynamic filters
  lookupUrl?: string;
  optionKey?: string | string[]; // Key for prefetched options

  // For options sourced from a global Pinia store (cross-capability data)
  // Resolved by useGlobalOptions composable + a registered provider.
  globalOptionsKey?: string;
  config?: {
    labelField: string;
    valueField: string;
    responseLimit?: number;
  };

  // Dependencies for dynamic fetching
  dependencies?: Record<string, string>;

  // List of filter keys to clear dependent filters if the current filter is changed
  clearDependencies?: string[];
  // disable the filter until parent filter is selected
  disableUntilParentSelected?: boolean;
  // enable dynamic fetching if the parent filter is updated
  enableFetchDependentOptions?: boolean;

  // Static options (if not using lookupUrl)
  options?: FilterOption[];

  // Optional grouping/sectioning
  sectionLabel?: string;
  sectionIcon?: string;
  groupId?: string;
  groupLabel?: string;

  //Search parameters for typeahead filters
  searchParam?: string;
  debounce?: number;

  // Additional properties for specific filter types
  width?: string; // e.g., "100px", "200px"
  dateInputWidth?: string; // width of the custom-range from/to date inputs, e.g. "170px" (default 150px)
  optionLabelFormat?: string; // e.g., '${name} (${code})'
  maxOptions?: number; // maximum number of options for typeahead
  minChars?: number; // minimum characters to trigger search
  normaliseText?: boolean;
  /** Opt-in: renders a leading icon for each option of a multi-select (e.g. status glyphs) */
  optionIcon?: (value: string | number) => { icon: string; color?: string } | undefined;
  subLabelKey?: string; // key for sub-label in options
  inputIcon?: string; // icon for the input field
  clearButton?: boolean; // whether to show a clear button
  yearcap?: number; // whether to apply a year cap for date filters
  minDateKey?: string; // key of another filter in the same zone whose value sets the minimum selectable date
  maxDateKey?: string; // key of another filter in the same zone whose value sets the maximum selectable date
  labelTooltip?: boolean; // whether to show a tooltip for the label
  freetexttagging?: boolean; // whether to allow free text tagging
  paginatedOptions?: boolean; // enable infinite scroll pagination for typeahead options
  inputParams?: { [key: string]: any }; // additional parameters for the lookup
  min?: string | number;
  max?: string | number;
  step?: number; // step value for number stepper
  description?: string; // description text for filter
  disableTyping?: boolean;
  includeTime?: boolean;
  isHotUpdate?: boolean;
  layoutDirection?: "row" | "column"; // layout direction for the filter component
  disableOnOtherFilters?: string[]; // list of filter keys to disable this filter if any of the listed filters have a value

  // function to extract meta data for payload to be sent
  extractPayloadFromOption?: (option: any) => Record<string, any>;

  // Optional suffix to append to the zone prefix for store storage (e.g. "_config")
  storeZoneSuffix?: string;
  disabled?: boolean; // whether the filter is disabled
  // Hides the stored selection without clearing it.
  // Does not disable interaction; set `disabled` as well for an empty, disabled filter.
  notApplicable?: boolean;
}

/** The valid filter component types */
export type FilterComponentType = (typeof FILTER_TYPES)[keyof typeof FILTER_TYPES];
// add more types like 'checkbox', 'calendar', etc.

/** Complete definition of a recipe — an array of filters */
export type FilterRecipe = FilterDefinition[];

/** Store structure for selected filters */
export type SelectedFiltersMap = Record<string, FilterSelection | string>;

/** Store structure for applied filters */
export type AppliedFiltersMap = Record<string, string>;

export type FilterType = FilterTypeEnum.QUICK_FILTERS | FilterTypeEnum.PANEL_FILTERS;

export enum FilterTypeEnum {
  QUICK_FILTERS = "quick-filters",
  PANEL_FILTERS = "panel-filters"
}

export enum SelectDateRangeOption {
  QUARTER_TO_DATE = "quarterToDate",
  YEAR_TO_DATE = "yearToDate",
  LAST_12_MONTHS = "last12Months",
  CUSTOM_RANGE = "customRange",
  CUSTOM_MONTH_RANGE = "customMonthRange"
}

export enum RelativeDateRangeOption {
  PLUS_MINUS_7 = "plusMinus7",
  PLUS_MINUS_14 = "plusMinus14",
  CUSTOM_DAYS = "customDays",
  SPECIFIC_RANGE = "specificRange"
}

export type Option = { label: string; value: any };

export interface FilterStoreObject {
  label: string;
  value: string | number | Option[] | Option;
  filterKey: string;
  filterType: FilterTypeEnum;
  // Optional grouping/sectioning
  sectionLabel?: string;
  sectionIcon?: string;
  groupId?: string;
  groupLabel?: string;
  filterLabel?: string;
  // Dependencies for dynamic fetching
  dependencies?: Record<string, string>;
  clearDependencies?: string[];
  dateRange?: {
    startDate?: string; // ISO date string
    endDate?: string; // ISO date string
  };
  payload?: any;
  isValid?: boolean; // Set to false if the filter has invalid internal state
}

export type FetchFilterOptionConfig = {
  isMultiSelect: boolean;
  considerLabel?: boolean;
};

/**
 * Configuration for different filter types and their processing parameters
 */
export type FilterProcessingConfig = {
  fetchFunction: (params: Record<string, string>) => Promise<Option[]>;
  isMultiSelect: boolean;
  matchByLabel?: boolean;
  globalCache?: Option[];
};

export type FilterProcessingProps = {
  queryParams: Record<string, any>;
  fieldRecipe: FilterDefinition;
  filterKey: string;
  fetchedOptions?: Option[];
  config?: {
    considerLabel: boolean;
    convertCase: boolean;
  };
};
